/**
 * Task Detail Page
 * Shows full task information with linked entities
 */
import React, { useState, useEffect, useCallback } from 'react';
import { crmAPI } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
// Import shared components from the Unified Detail Page System
import { 
  DetailHeader, 
  DetailPageLayout, 
  ContentPanel, 
  StatusPill,
  STATUS_CONFIGS 
} from '../../components/detail-system';
import {
  ClipboardList,
  User,
  Calendar,
  Clock,
  CheckCircle,
  AlertTriangle,
  Building2,
  TrendingUp,
  FileText,
  ShoppingCart,
  Loader2,
  Flag
} from 'lucide-react';

const PRIORITIES = {
  low: { label: 'Low', color: 'bg-gray-100 text-gray-700' },
  med: { label: 'Medium', color: 'bg-yellow-100 text-yellow-700' },
  high: { label: 'High', color: 'bg-red-100 text-red-700' }
};

const STATUSES = {
  open: { label: 'Open', color: 'bg-blue-100 text-blue-700' },
  done: { label: 'Completed', color: 'bg-green-100 text-green-700' },
  canceled: { label: 'Canceled', color: 'bg-gray-100 text-gray-700' }
};

export default function TaskDetailPage({ 
  taskId, 
  onBack,
  onNavigateToCustomer,
  onNavigateToLead,
  onNavigateToQuote,
  onNavigateToOrder
}) {
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [completing, setCompleting] = useState(false);

  // Fetch task data
  const fetchTask = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await crmAPI.getTasks({ limit: 1 });
      // Find the specific task
      const allTasks = await crmAPI.getTasks({ limit: 200 });
      const found = (allTasks.tasks || []).find(t => t.id === taskId);
      if (found) {
        setTask(found);
      } else {
        setError('Task not found');
      }
    } catch (err) {
      console.error('Failed to fetch task:', err);
      setError('Failed to load task details');
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    if (taskId) {
      fetchTask();
    }
  }, [taskId, fetchTask]);

  // Handle task completion
  const handleComplete = async () => {
    try {
      setCompleting(true);
      await crmAPI.completeTask(taskId);
      fetchTask();
    } catch (err) {
      console.error('Failed to complete task:', err);
      alert('Failed to complete task: ' + err.message);
    } finally {
      setCompleting(false);
    }
  };

  // Check if overdue
  const isOverdue = task?.status === 'open' && task?.due_at && new Date(task.due_at) < new Date();

  // Format due date
  const formatDueDate = (date) => {
    if (!date) return 'No due date';
    const d = new Date(date);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (d.toDateString() === today.toDateString()) {
      return `Today at ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (d.toDateString() === tomorrow.toDateString()) {
      return `Tomorrow at ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }
    return d.toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="task-detail-loading">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="flex flex-col items-center justify-center h-96" data-testid="task-detail-error">
        <p className="text-red-600 mb-4">{error || 'Task not found'}</p>
        <Button onClick={onBack}>Go Back</Button>
      </div>
    );
  }

  const priorityInfo = PRIORITIES[task.priority] || PRIORITIES.med;
  const statusInfo = STATUSES[task.status] || STATUSES.open;

  // Task Status Config for DetailHeader
  const TASK_STATUS_CONFIG = {
    open: { label: 'Open', color: 'bg-blue-100 text-blue-700' },
    done: { label: 'Completed', color: 'bg-green-100 text-green-700' },
    canceled: { label: 'Canceled', color: 'bg-gray-100 text-gray-700' }
  };

  // Header Actions
  const headerPrimaryActions = task.status === 'open' ? [
    {
      label: 'Mark Complete',
      icon: CheckCircle,
      onClick: handleComplete,
      testId: 'mark-complete-btn'
    }
  ] : [];

  // Sidebar content
  const sidebarContent = (
    <>
      {/* Linked Objects Card */}
      <ContentPanel title="Linked To" icon={Building2} testId="linked-to-panel">
        <div className="space-y-3">
          {task.account_id && (
            <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Customer</p>
                  <p className="text-sm font-mono">{task.account_id}</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => onNavigateToCustomer?.(task.account_id)}
                data-testid="view-linked-customer-btn"
              >
                View
              </Button>
            </div>
          )}
          
          {task.lead_id && (
            <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Lead</p>
                  <p className="text-sm font-mono">{task.lead_id}</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => onNavigateToLead?.(task.lead_id)}
                data-testid="view-linked-lead-btn"
              >
                View
              </Button>
            </div>
          )}
          
          {task.quote_id && (
            <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Quote</p>
                  <p className="text-sm font-mono">{task.quote_id}</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => onNavigateToQuote?.(task.quote_id)}
                data-testid="view-linked-quote-btn"
              >
                View
              </Button>
            </div>
          )}
          
          {task.order_id && (
            <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <ShoppingCart className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Order</p>
                  <p className="text-sm font-mono">{task.order_id}</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => onNavigateToOrder?.(task.order_id)}
                data-testid="view-linked-order-btn"
              >
                View
              </Button>
            </div>
          )}
          
          {!task.account_id && !task.lead_id && !task.quote_id && !task.order_id && (
            <p className="text-sm text-gray-400 text-center py-4">
              Not linked to any entity
            </p>
          )}
        </div>
      </ContentPanel>

      {/* Timestamps Card */}
      <ContentPanel title="Timestamps" icon={Calendar} testId="timestamps-panel">
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Created</span>
            <span className="text-gray-700">{new Date(task.created_at).toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Updated</span>
            <span className="text-gray-700">{new Date(task.updated_at).toLocaleString()}</span>
          </div>
          {task.completed_at && (
            <div className="flex justify-between">
              <span className="text-gray-500">Completed</span>
              <span className="text-green-700">{new Date(task.completed_at).toLocaleString()}</span>
            </div>
          )}
        </div>
      </ContentPanel>
    </>
  );

  return (
    <DetailPageLayout
      headerProps={{
        entityName: task.title,
        subtitle: `${task.id} • Created ${new Date(task.created_at).toLocaleDateString()}`,
        entityIcon: ClipboardList,
        status: task.status,
        statusConfig: TASK_STATUS_CONFIG,
        onBack,
        backLabel: 'Back',
        primaryActions: headerPrimaryActions,
        testId: 'task-detail-header'
      }}
      sidebarContent={sidebarContent}
      loading={completing}
      testId="task-detail-page"
    >
      {/* Task Details Card */}
      <ContentPanel title="Task Details" icon={ClipboardList} testId="task-details-panel">
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Badge className={priorityInfo.color}>
              <Flag className="w-3 h-3 mr-1" />
              {priorityInfo.label} Priority
            </Badge>
            <StatusPill 
              status={task.status}
              config={TASK_STATUS_CONFIG}
              testId="task-status-pill"
            />
            {isOverdue && (
              <Badge className="bg-red-100 text-red-700">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Overdue
              </Badge>
            )}
          </div>
          
          <div className={`flex items-center gap-3 p-3 rounded-lg ${
            isOverdue ? 'bg-red-50 border border-red-200' : 'bg-blue-50'
          }`}>
            {isOverdue ? (
              <AlertTriangle className="w-5 h-5 text-red-600" />
            ) : (
              <Clock className="w-5 h-5 text-blue-600" />
            )}
            <div>
              <p className={`text-xs ${isOverdue ? 'text-red-600' : 'text-blue-600'} font-medium`}>
                {isOverdue ? 'OVERDUE' : 'Due Date'}
              </p>
              <p className={`font-medium ${isOverdue ? 'text-red-800' : 'text-blue-800'}`}>
                {formatDueDate(task.due_at)}
              </p>
            </div>
          </div>

          {task.description && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Description</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 p-3 rounded-lg">
                {task.description}
              </p>
            </div>
          )}

          {task.completed_at && (
            <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <div>
                <p className="text-xs text-green-600 font-medium">Completed</p>
                <p className="text-sm text-green-800">
                  {new Date(task.completed_at).toLocaleString()}
                </p>
              </div>
            </div>
          )}
        </div>
      </ContentPanel>

      {/* Ownership Card */}
      <ContentPanel title="Assignment" icon={User} testId="assignment-panel">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-500 mb-1">Assigned To</p>
            <div className="flex items-center gap-2">
              <User className="w-3.5 h-3.5 text-gray-400" />
              <p className="text-sm text-gray-900">{task.owner_user_id || 'Unassigned'}</p>
            </div>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Created By</p>
            <div className="flex items-center gap-2">
              <User className="w-3.5 h-3.5 text-gray-400" />
              <p className="text-sm text-gray-900">{task.created_by_user_id || 'Unknown'}</p>
            </div>
          </div>
        </div>
      </ContentPanel>
    </DetailPageLayout>
  );
}
